/**
 * ABB Weather Timepoint Navigation Actions
 *
 * Adds previous/next arrows to ABB weather layers in the LayerControl,
 * allowing users to navigate through available timepoints (e.g. forecast
 * hours or radar observations).
 *
 * Follows the same pattern as GPSLostActions and CoverageActions:
 * a custom hook extracts all logic, and the component is purely
 * presentational — delegating rendering to PeriodNavigationActions.
 *
 * ## How ABB timepoints work
 *
 * Each ABB weather layer has a `timepoints` dictionary in its metadata,
 * keyed by a zero-based string index ("0", "1", "2", …). The index is
 * passed as `?timeshift=N` when requesting tiles. Different layers have
 * different intervals:
 *
 * - Marine forecasts: every 3 hours, ~57 timepoints (7 days ahead)
 * - Radar observations: every 5 minutes, ~72 timepoints (6 hours back)
 *
 * On layer toggle, we auto-select the timepoint closest to "now".
 * These arrows let users scrub forward/backward from there.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import TileLayer from 'ol/layer/Tile';
import { XYZ } from 'ol/source';
import { toast } from 'sonner';
import { Icons } from '@ais-ui/ui-core';
import { PiCheckCircle } from 'react-icons/pi';
import { LayerItem, useLayers } from '@ais-ui/react-openlayers';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { PeriodNavigationActions } from './PeriodNavigationActions';
import {
  ABBLayerMetadata,
  ABBTimepoint,
  buildABBTileUrl,
  findNearestTimeshift,
} from './Actions/useABBWeatherLayers';
import { useWeatherLegend } from '../WeatherLegend';
import { useABBTileLoadingProgress } from './hooks/useABBTileLoadingProgress';

// ==================== Hook ====================

interface TimepointNavigationState {
  currentIndex: number;
  timepointCount: number;
  sortedEntries: [string, ABBTimepoint][];
  isReady: boolean;
}

/**
 * Custom hook that encapsulates all timepoint navigation logic.
 *
 * Reads metadata from the React Query cache (already fetched on layer toggle)
 * and manages the current timeshift index. On navigation, updates the
 * OpenLayers XYZ source URL to load tiles for the new timepoint.
 */
function useABBTimepointNavigation(
  layer: LayerItem,
): TimepointNavigationState & {
  goNext: () => void;
  goPrevious: () => void;
  getTimepointLabel: (index: number) => string;
} {
  const queryClient = useQueryClient();
  const { getLayerById } = useLayers();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const abbLayerId = layer.properties?.abbLayerId as string | undefined;

  // Read metadata from React Query cache — no extra fetch needed
  const metadata = abbLayerId
    ? queryClient.getQueryData<ABBLayerMetadata>([
        'abb',
        'layer-metadata',
        abbLayerId,
      ])
    : undefined;

  // Sort timepoint entries by their numeric key so navigation is sequential
  const sortedEntries = useMemo(() => {
    if (!metadata?.timepoints) return [];
    return Object.entries(metadata.timepoints).sort(
      ([a], [b]) => Number(a) - Number(b),
    );
  }, [metadata]);

  const timepointCount = sortedEntries.length;

  // Initialize current index from the timepoint closest to now
  const initialIndex = useMemo(() => {
    if (!metadata?.timepoints) return 0;
    const nearestKey = findNearestTimeshift(metadata.timepoints);
    const idx = sortedEntries.findIndex(([key]) => key === nearestKey);
    return idx >= 0 ? idx : 0;
  }, [metadata, sortedEntries]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Format a timepoint's datetime for tooltip display
  const getTimepointLabel = useCallback(
    (index: number): string => {
      if (index < 0 || index >= sortedEntries.length) return '';
      const [, timepoint] = sortedEntries[index];
      const date = new Date(timepoint.timepoint);
      return formatDateTimeWithTimeZone(date);
    },
    [sortedEntries, formatDateTimeWithTimeZone],
  );

  // Update the OpenLayers tile source URL to show the new timepoint
  const updateTileSource = useCallback(
    (newIndex: number) => {
      if (!abbLayerId) return;

      const timeshiftKey = sortedEntries[newIndex]?.[0];
      if (!timeshiftKey) return;

      const layerItem = getLayerById(`abb-weather-${abbLayerId}`);
      if (!layerItem) return;

      const olLayer = layerItem.layer as TileLayer<XYZ>;
      const source = olLayer.getSource() as XYZ;
      source.setUrl(buildABBTileUrl(abbLayerId, timeshiftKey));
    },
    [abbLayerId, sortedEntries, getLayerById],
  );

  const goNext = useCallback(() => {
    if (currentIndex >= timepointCount - 1) return;
    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    updateTileSource(nextIndex);
  }, [currentIndex, timepointCount, updateTileSource]);

  const goPrevious = useCallback(() => {
    if (currentIndex <= 0) return;
    const previousIndex = currentIndex - 1;
    setCurrentIndex(previousIndex);
    updateTileSource(previousIndex);
  }, [currentIndex, updateTileSource]);

  return {
    currentIndex,
    timepointCount,
    sortedEntries,
    isReady: timepointCount > 1,
    goNext,
    goPrevious,
    getTimepointLabel,
  };
}

// ==================== Constants ====================

const WEATHER_TOAST_ID = 'abb-weather-tile-progress';

/**
 * Shared mutable progress state read by the toast's DOM updater.
 * Lives outside React to avoid any state/render interactions.
 */
const tileProgress = { loaded: 0, total: 0, active: false };

// ==================== Component ====================

interface ABBWeatherActionsProps {
  layer: LayerItem;
  setDisableDragNnDropTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  isDragging: boolean;
  variant?: 'external' | 'inline';
}

export const ABBWeatherActions = ({
  layer,
  setDisableDragNnDropTooltip,
  isDragging,
  variant = 'external',
}: ABBWeatherActionsProps) => {
  const {
    currentIndex,
    timepointCount,
    isReady,
    goNext,
    goPrevious,
    getTimepointLabel,
  } = useABBTimepointNavigation(layer);

  const { setTimepointLabel } = useWeatherLegend();

  const abbLayerId = layer.properties?.abbLayerId as string | undefined;

  // Track tile loading progress via OL events
  const olTileLayer = layer.layer as TileLayer<XYZ>;
  const { isLoading, loaded, total, errored } =
    useABBTileLoadingProgress(olTileLayer);

  // Sync progress to module-level mutable object (read by DOM interval)
  tileProgress.loaded = loaded;
  tileProgress.total = total;

  // Refs for the success toast to read final values without being a dep
  const totalRef = useRef(total);
  const erroredRef = useRef(errored);
  totalRef.current = total;
  erroredRef.current = errored;

  // Show loading toast + start DOM progress interval on idle→loading transition.
  // Only depends on isLoading so the interval isn't killed by total/errored changes.
  const prevIsLoadingRef = useRef(false);
  useEffect(() => {
    const wasLoading = prevIsLoadingRef.current;
    prevIsLoadingRef.current = isLoading;

    if (!wasLoading && isLoading) {
      // Transition: idle → loading.
      tileProgress.active = true;

      setTimeout(() => {
        toast('Loading weather tiles...', {
          id: WEATHER_TOAST_ID,
          duration: Infinity,
          closeButton: true,
          icon: <Icons.spinner className="size-4 animate-spin" />,
          className: 'weather-tile-progress-toast',
        });
      }, 0);

      // Update progress text via direct DOM manipulation — completely
      // outside React's render cycle and Sonner's flushSync.
      const interval = setInterval(() => {
        if (!tileProgress.active) {
          clearInterval(interval);
          return;
        }
        const { loaded: l, total: t } = tileProgress;
        const el = document.querySelector(
          '.weather-tile-progress-toast [data-title]',
        );
        if (el && t > 0) {
          el.textContent = `Loading weather tiles: ${l}/${t}`;
        }
      }, 200);

      return () => clearInterval(interval);
    } else if (wasLoading && !isLoading) {
      // Transition: loading → done.
      tileProgress.active = false;
      const t = totalRef.current;
      const e = erroredRef.current;

      if (t > 0) {
        const description =
          e > 0 ? `Loaded ${t - e}/${t} tiles (${e} failed)` : undefined;

        setTimeout(() => {
          toast.success('Weather tiles loaded', {
            id: WEATHER_TOAST_ID,
            description,
            duration: 3000,
            closeButton: true,
            icon: <PiCheckCircle className="size-4 text-green-500" />,
          });
        }, 0);
      }
    }
  }, [isLoading]);

  // Dismiss toast if layer is removed while loading
  useEffect(() => {
    return () => {
      toast.dismiss(WEATHER_TOAST_ID);
    };
  }, []);

  // Update legend timestamp when timepoint changes
  useEffect(() => {
    if (isReady && abbLayerId) {
      setTimepointLabel(abbLayerId, getTimepointLabel(currentIndex));
    }
  }, [currentIndex, isReady, abbLayerId, getTimepointLabel, setTimepointLabel]);

  // Derive disabled states from current index and count
  const isPreviousDisabled = currentIndex <= 0;
  const isNextDisabled = currentIndex >= timepointCount - 1;

  // Derive tooltips from adjacent timepoint labels + boundaries
  const tooltips = useMemo(() => {
    const previousLabel = getTimepointLabel(currentIndex - 1);
    const nextLabel = getTimepointLabel(currentIndex + 1);
    const earliestLabel = getTimepointLabel(0);
    const latestLabel = getTimepointLabel(timepointCount - 1);

    return {
      previousTooltip: previousLabel
        ? `Switch to ${previousLabel}\nEarliest: ${earliestLabel}`
        : 'No earlier forecast available',
      nextTooltip: nextLabel
        ? `Switch to ${nextLabel}\nLatest: ${latestLabel}`
        : 'No later forecast available',
    };
  }, [currentIndex, timepointCount, getTimepointLabel]);

  return (
    <PeriodNavigationActions
      onNext={goNext}
      onPrevious={goPrevious}
      isLoading={isLoading}
      isPreviousDisabled={isPreviousDisabled}
      isNextDisabled={isNextDisabled}
      previousTooltip={tooltips.previousTooltip}
      nextTooltip={tooltips.nextTooltip}
      shouldShow={isReady}
      setDisableDragNnDropTooltip={setDisableDragNnDropTooltip}
      isDragging={isDragging}
      variant={variant}
    />
  );
};
